import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getEnvConfig } from '@configs/env';
import { ProviderIcon } from '@configs/providerIcons';
import styled from '@emotion/styled';
import { Avatar, Checkbox, TextField, useTheme } from '@mui/material';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { CheckboxIcon } from '@icons/CheckboxIcon';
import { ErrorIcon } from '@icons/ErrorIcon';
import { useGetSubscriptionConnection } from '@queryHooks/useSubscriptionConnection';
import { useGetSMPConnections } from '@queryHooks/useTenant';
import { insertAt } from '@utils/CommonUtil';
import { ConnectorActionModel } from '@repositories/productConnectorRepository/models/ConnectorActionModel';
import { InputDataPopup } from './InputDataPopup';
import { ResourceValueInput } from './ResourceValueInput';

type ShowData = {
  isShow: boolean;
  index?: number;
  cursorPos?: number; // 텍스트필드 내 커서 위치
};

export const ActionConfig = observer(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { tenantId } = useParams();
  const { SMP_PROVIDER_NAME } = getEnvConfig();
  const { workflowDetailStore } = useStore();
  const isEditTable = workflowDetailStore.checkEditable();
  const [showData, setShowData] = useState<ShowData>({ isShow: false });

  const actionNode = workflowDetailStore.findNode(workflowDetailStore.curNodeId ?? '');
  const actionModel = actionNode?.data?.actionModel;
  const connectionModel = actionNode?.data?.connectionModel;
  const subscription = connectionModel?.subscription;
  const isSmpAction = actionNode?.data.isSmpAction;
  const isConditionalBranchAction = actionNode?.data.actionModel?.type === 'IF_ACTION';

  const { data: subscriptionConnection } = useGetSubscriptionConnection(connectionModel?.subscription.id ?? '', {
    enabled: !!connectionModel?.subscription.id,
  });
  const { data: smpConnection } = useGetSMPConnections(tenantId as string, {
    enabled: isSmpAction,
  });

  // 커넥션 액션을 메모이제이션 처리해서 성능 최적화,
  // 여기에 추가로, 다음에 나오는 모델 초기화의 useEffect 의존성에 추가하기 위해서임.
  const connectorAction = useMemo(() => {
    if (isSmpAction) {
      return smpConnection?.connector.actions.find(action => action.method === actionModel?.actionMethod);
    }
    if (isConditionalBranchAction) {
      return new ConnectorActionModel({
        description: t('Workflow.action.condition_description'),
        inputParameters: [
          {
            name: 'expr',
            displayName: t('Workflow.action.condition_expr'),
            inputType: 'BOOLEAN',
          },
        ],
      });
    }
    return subscriptionConnection?.connection.connector.actions.find(
      action => action.method === actionModel?.actionMethod,
    );
  }, [
    actionModel?.actionMethod,
    isConditionalBranchAction,
    isSmpAction,
    smpConnection?.connector.actions,
    subscriptionConnection?.connection.connector.actions,
    t,
  ]);

  // 액션 설정에 들어왔을 때, 커넥터 액션 파라메터를 사용하여 액션 아귀먼트 목록을 초기화
  // 그렇지 않으면 파라메터와 아귀먼트가 맞지 않게 되면서 체크박스 변경 혹은 필드 값 수정이 안됨.
  useEffect(() => {
    if (!actionModel || !connectorAction) return;
    const actionArguments = actionModel.actionArguments;
    actionModel.actionArguments = connectorAction.inputParameters.map(inputParam => {
      const existingArgument = actionArguments?.find(arg => arg.argumentName === inputParam.name);
      return (
        existingArgument ?? {
          argumentName: inputParam.name,
          expression: '',
          expressionType: 'CUSTOM',
        }
      );
    });
  }, [actionModel, connectorAction]);

  const handleChangeCheckbox = (checked: boolean, argIndex: number) => {
    if (!workflowDetailStore.checkEditable() || !actionModel?.actionArguments?.[argIndex]) return;

    const currentValue = actionModel.actionArguments[argIndex].expression ?? '';

    if (checked) {
      // SIMPLE → CUSTOM 전환: #{field} → input['field']로 변환
      const spelMatch = currentValue.match(/^#{(.+)}$/);
      if (spelMatch) {
        const fieldName = spelMatch[1];
        actionModel.actionArguments[argIndex].expression = `input['${fieldName}']`;
      }
      // 리소스 ID인 경우는 그대로 유지 (이미 ID로 저장되어 있음)
    } else {
      // CUSTOM → SIMPLE 전환: input['field'] → #{field}로 변환
      const inputMatch = currentValue.match(/^input\['(.+)'\]$/);
      if (inputMatch) {
        const fieldName = inputMatch[1];
        actionModel.actionArguments[argIndex].expression = `#{${fieldName}}`;
      }
      // 그 외는 그대로 유지
    }

    actionModel.actionArguments[argIndex].expressionType = checked ? 'CUSTOM' : 'SIMPLE';
  };

  const handleClickInputDataPopup = (
    type: 'action' | 'trigger',
    field: string,
    displayName: string,
    argIndex: number,
  ) => {
    if (!workflowDetailStore.checkEditable() || !actionModel?.actionArguments) return;

    if (type === 'trigger') {
      const currentArgument = actionModel.actionArguments[argIndex];
      const isCustom = currentArgument.expressionType === 'CUSTOM';
      console.log(field);
      // SpEL 직접 입력 체크 여부에 따라 형식 결정
      const spelExpression = isCustom ? `input['${field}']` : `#{${field}}`;

      // 해당 parameter의 resourceType 확인
      const param = connectorAction?.inputParameters?.find(p => p.name === currentArgument?.argumentName);

      // resourceType='subscription'이면 덮어쓰기, 아니면 커서 위치에 삽입
      if (param?.resourceType === 'subscription') {
        actionModel.actionArguments[argIndex].expression = spelExpression;
      } else {
        actionModel.actionArguments[argIndex].expression = insertAt(
          actionModel.actionArguments[argIndex].expression ?? '',
          showData.cursorPos,
          spelExpression,
        );
      }
    }
  };

  const handleCloseUnputDataPopup = () => {
    setShowData({ isShow: false, index: undefined });
  };

  const handleResourceConfirm = (
    spelExpression: string | null,
    resourceId: string | null,
    _resourceName: string | null,
    argIndex: number,
  ) => {
    if (!workflowDetailStore.checkEditable() || !actionModel?.actionArguments?.[argIndex]) return;

    // SPEL 표현식 또는 리소스 ID 중 하나만 저장 (덮어쓰기)
    const newValue = spelExpression || resourceId || '';
    console.log('🚀 ~ handleResourceConfirm ~ newValue:', newValue);

    actionModel.actionArguments[argIndex].expression = newValue;
    setShowData({ isShow: false, index: undefined });
  };

  const renderConfigTitle = () => {
    if (isSmpAction) {
      return (
        <>
          <ProviderIcon providerName={SMP_PROVIDER_NAME} width={56} height={56} />
          <AppInfo>
            <SmpApp>{t(`Provider_LOGO_${SMP_PROVIDER_NAME}`)}</SmpApp>
          </AppInfo>
        </>
      );
    }
    if (isConditionalBranchAction) {
      return (
        <>
          <Avatar sx={{ width: '56px', height: '56px' }}> IF</Avatar>
          <AppInfo />
        </>
      );
    }
    return (
      <>
        <Avatar src={subscription?.product.iconUrl} sx={{ width: '56px', height: '56px' }} />
        <AppInfo>
          <AppName>{subscription?.product.name}</AppName>
          <SubscriptionName>{subscription?.name}</SubscriptionName>
        </AppInfo>
      </>
    );
  };

  console.log(actionModel?.actionArguments);

  return (
    <Container isEditTable={isEditTable}>
      {!isEditTable && (
        <>
          <ToolbarName>{actionModel?.actionName}</ToolbarName>
          <App>{renderConfigTitle()}</App>
        </>
      )}
      <Body>
        {!isEditTable && !!connectorAction?.description && (
          <ActionDescription>{connectorAction.description}</ActionDescription>
        )}
        {
          // inputParameter가 없는 경우
          connectorAction?.inputParameters.length === 0 && (
            <EmptyInputParam>
              <EmptyInputIcon>
                <ErrorIcon color={theme.colors['ic-primary-light']} />
              </EmptyInputIcon>
              <EmptyInputDescription>
                <span>{t('Workflow_Create_40')}</span>
                <EmptyInputDescriptionSub>{t('Workflow_Create_41')}</EmptyInputDescriptionSub>
              </EmptyInputDescription>
            </EmptyInputParam>
          )
        }
        <InputParams>
          {connectorAction?.inputParameters && connectorAction?.inputParameters?.length > 0 && (
            <>
              <Hr />
              <ToolbarName>{t('Workflow_Create_76')}</ToolbarName>
              {connectorAction?.inputParameters.map(param => {
                const argIndex = actionModel?.actionArguments?.findIndex(arg => arg.argumentName === param.name) ?? 0;
                return (
                  <InputParam key={param.name}>
                    <InputParamHeader>
                      <InputParamLabel>
                        {param.displayName || param.name}
                        {param.isRequired && <InputParamLabelStar>*</InputParamLabelStar>}
                      </InputParamLabel>
                      <InputParamCustom>
                        <Checkbox
                          icon={<CheckboxIcon width={16} height={16} color={theme.colors['border-gray-light']} />}
                          checkedIcon={
                            <CheckboxIcon
                              width={16}
                              height={16}
                              color={theme.colors['ic-white']}
                              bgColor={theme.colors['bg-primary']}
                            />
                          }
                          checked={actionModel?.actionArguments?.[argIndex]?.expressionType === 'CUSTOM'}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                            if (checked) {
                              setShowData(prev => {
                                return { ...prev, isShow: false };
                              });
                            }
                            handleChangeCheckbox(checked, argIndex);
                          }}
                          disabled={param.inputType === 'ARRAY'}
                        />
                        <span>{t('Workflow.action.spel_input')}</span>
                      </InputParamCustom>
                    </InputParamHeader>
                    {param.resourceType === 'subscription' ? (
                      <ResourceValueInput
                        value={actionModel?.actionArguments?.[argIndex]?.expression ?? ''}
                        expressionType={actionModel?.actionArguments?.[argIndex]?.expressionType}
                        isEditable={workflowDetailStore.checkEditable()}
                        onSelect={cursorPos => {
                          if (!workflowDetailStore.checkEditable()) return;
                          setShowData({
                            isShow: true,
                            index: argIndex,
                            cursorPos,
                          });
                        }}
                        onChange={value => {
                          if (!workflowDetailStore.checkEditable() || !actionModel?.actionArguments?.[argIndex]) return;
                          actionModel.actionArguments[argIndex].expression = value;
                        }}
                      />
                    ) : (
                      <TextField
                        value={actionModel?.actionArguments?.[argIndex]?.expression ?? ''}
                        fullWidth
                        multiline
                        placeholder={t('Workflow.action.select_value')}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (!workflowDetailStore.checkEditable() || !actionModel?.actionArguments?.[argIndex]) return;
                          actionModel.actionArguments[argIndex].expression = e.target.value;
                        }}
                        onSelect={(e: React.SyntheticEvent<HTMLDivElement>) => {
                          if (!workflowDetailStore.checkEditable()) return;
                          const target = e.target as HTMLInputElement;
                          setShowData({
                            isShow: true,
                            index: argIndex,
                            cursorPos: target.selectionStart ?? undefined,
                          });
                        }}
                        autoComplete="off"
                        InputProps={{
                          readOnly: !workflowDetailStore.checkEditable(),
                        }}
                      />
                    )}
                  </InputParam>
                );
              })}
            </>
          )}

          {showData.isShow &&
            (() => {
              // 현재 편집 중인 파라미터의 resourceType 찾기
              const currentArgument = actionModel?.actionArguments?.[showData.index as number];
              const currentParam = connectorAction?.inputParameters.find(
                param => param.name === currentArgument?.argumentName,
              );

              return (
                <InputDataPopup
                  triggerId={workflowDetailStore.triggerNode?.data.baseTriggerModel?.id}
                  paramResourceType={currentParam?.resourceType}
                  paramName={currentParam?.name}
                  onClick={handleClickInputDataPopup}
                  onResourceConfirm={handleResourceConfirm}
                  onClose={handleCloseUnputDataPopup}
                  index={showData.index as number}
                />
              );
            })()}
        </InputParams>
      </Body>
    </Container>
  );
});

const Container = styled.div<{ isEditTable: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  background: inherit;
  padding: 36px 32px;
  ${({ isEditTable }) => isEditTable && 'padding: 0px;'}
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: inherit;
  border: 0px;
  padding: 28px 0 20px;
  position: relative;
`;

const ToolbarName = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-bottom: 18px;
  ${fonts.Headline7};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const App = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 2px 0;

  & .MuiAvatar-root {
    border-radius: 8px;
  }
`;

const AppInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SmpApp = styled.span`
  ${fonts.Headline4};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const AppName = styled.div`
  ${fonts.Body1};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const SubscriptionName = styled.strong`
  ${fonts.Headline4};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const ActionDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-radius: 4px;
  background-color: ${props => props.theme.colors['bg-primary-lighter']};
  padding: 18px 24px;
  ${fonts.Body4};
  color: ${props => props.theme.colors['text-primary']};
  margin-bottom: 12px;
`;

const EmptyInputParam = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  height: 230px;
  background-color: ${props => props.theme.colors['bg-gray-lighter']};
  margin-top: 12px;
`;

const EmptyInputIcon = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 100%;
  background: ${props => props.theme.colors['bg-primary-lighter']};
`;

const EmptyInputDescription = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${fonts.Body2};
`;

const EmptyInputDescriptionSub = styled.span`
  color: ${props => props.theme.colors['text-gray-sub-dark']};
`;

const InputParams = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputParam = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 6px;
`;

const InputParamHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const InputParamLabel = styled.label`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  ${fonts.Headline8};
  gap: 4px;
`;

const InputParamLabelStar = styled.span`
  color: ${props => props.theme.colors['text-red-light']};
`;

const InputParamCustom = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  ${fonts.Body2};
`;

const Hr = styled.div`
  width: 100%;
  height: 1px;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  margin-bottom: 16px;
`;
